import { useMemo, useState } from "react";
import { useEditorStore, type ViewMode } from "../app/store";
import { Icon } from "../components/Icon";
import type { ControlDefinition } from "../domain/instrument";
import {
  createPerformanceTransitionItems,
  type PerformanceTransitionItem,
} from "../domain/performance-transition";
import { getNormalledRouteStates, validateSignalPatch } from "../domain/signal-graph";
import { utilityNodes, workspaceDefinition } from "./workspace-ports";

type RecallItem = {
  id: string;
  kind: "control" | "cable" | "normal" | "setting";
  action?: PerformanceTransitionItem["action"];
  targetId: string;
  revealTargetId?: string;
  sectionId?: string;
  label: string;
  detail: string;
  colour?: string;
};

type RecallSession = {
  items: RecallItem[];
  completedIds: string[];
  currentIndex: number;
  status: "running" | "complete";
  mode: "snapshot" | "transition";
  targetStateId?: string;
  targetStateName?: string;
};

function nextIncomplete(items: RecallItem[], completedIds: string[], from: number) {
  for (let offset = 1; offset <= items.length; offset += 1) {
    const index = (from + offset) % items.length;
    if (!completedIds.includes(items[index]!.id)) return index;
  }
  return from;
}

export function RecallDock() {
  const {
    activeDocument,
    applyPerformanceState,
    cables,
    controlValues,
    focusSection,
    recallOpen,
    select,
    setInspectorTab,
    setViewMode,
    toggleRecall,
  } = useEditorStore();
  const [session, setSession] = useState<RecallSession | null>(null);
  const [selectedStateId, setSelectedStateId] = useState("");
  const definition = useMemo(() => workspaceDefinition(activeDocument), [activeDocument]);
  const utilities = useMemo(() => utilityNodes(activeDocument), [activeDocument]);

  const portNames = useMemo(
    () => new Map(definition.ports.map((port) => [port.id, port.label])),
    [definition],
  );
  const changedControls = definition.controls.filter(
    (control) =>
      controlValues[control.id] !== undefined &&
      Math.abs(controlValues[control.id]! - control.defaultValue) > 0.001,
  );
  const normalStates = getNormalledRouteStates(definition, cables, controlValues);
  const brokenNormals = normalStates.filter((state) => state.status === "broken");
  const warnings = validateSignalPatch(definition, cables);
  const selectedState =
    activeDocument.performanceStates.find((state) => state.id === selectedStateId) ??
    activeDocument.performanceStates[0] ??
    null;
  const transitionItems = useMemo(
    () =>
      selectedState
        ? createPerformanceTransitionItems(
            definition,
            {
              controlValues,
              cables,
              customSettings: activeDocument.customSettings,
            },
            selectedState,
          )
        : [],
    [activeDocument.customSettings, cables, controlValues, definition, selectedState],
  );

  const describeEndpoint = (endpoint: (typeof cables)[number]["endpointA"]) =>
    endpoint.kind === "loose"
      ? (endpoint.label ?? "Loose end")
      : `${portNames.get(endpoint.portId) ?? endpoint.portId}${
          endpoint.plugStyle === "stackable" ? " [stackable]" : ""
        }`;
  const describeControl = (control: ControlDefinition) => {
    const value = controlValues[control.id] ?? control.defaultValue;
    return control.options
      ? (control.options[Math.round(value * (control.options.length - 1))]?.label ?? "—")
      : control.type === "button"
        ? value >= 0.5
          ? "Active"
          : "Inactive"
        : `${Math.round(value * 100)}%`;
  };

  const startRecall = () => {
    const items: RecallItem[] = [
      ...changedControls.map((control) => ({
        id: `control:${control.id}`,
        kind: "control" as const,
        targetId: control.id,
        label: control.label,
        detail: `Set ${control.label} to ${describeControl(control)}.`,
      })),
      ...utilities.flatMap((utility) =>
        utility.controls.map((control) => ({
          id: `utility-control:${utility.id}:${control.id}`,
          kind: "control" as const,
          targetId: `utility:${utility.id}:control:${control.id}`,
          label: `${utility.displayName} — ${control.label}`,
          detail: `Set ${control.label.toLowerCase()} to ${Math.round(control.value * 100)}%.`,
        })),
      ),
      ...cables.map((cable) => ({
        id: `cable:${cable.id}`,
        kind: "cable" as const,
        targetId: cable.id,
        label: cable.label ? `Cable ${cable.number} — ${cable.label}` : `Cable ${cable.number}`,
        detail: `${cable.prepared ? "Prepared: " : ""}${describeEndpoint(
          cable.endpointA,
        )} → ${describeEndpoint(cable.endpointB)}`,
        colour: cable.colour,
      })),
      ...brokenNormals.map((state) => ({
        id: `normal:${state.route.id}`,
        kind: "normal" as const,
        targetId: state.route.id,
        label: state.route.label,
        detail: `Confirm this factory normal is overridden by ${state.brokenByCableIds.length} patch cable${state.brokenByCableIds.length === 1 ? "" : "s"}.`,
      })),
    ];
    setSession({
      items,
      completedIds: [],
      currentIndex: 0,
      status: items.length ? "running" : "complete",
      mode: "snapshot",
    });
  };

  const startTransition = () => {
    if (!selectedState) return;
    setSession({
      items: transitionItems,
      completedIds: [],
      currentIndex: 0,
      status: transitionItems.length ? "running" : "complete",
      mode: "transition",
      targetStateId: selectedState.id,
      targetStateName: selectedState.name,
    });
  };

  const toggleItem = (itemId: string, index: number, advance = false) => {
    setSession((current) => {
      if (!current) return current;
      const wasComplete = current.completedIds.includes(itemId);
      const completedIds = wasComplete
        ? current.completedIds.filter((id) => id !== itemId)
        : [...current.completedIds, itemId];
      const complete = current.items.length > 0 && completedIds.length === current.items.length;
      return {
        ...current,
        completedIds,
        currentIndex:
          advance && !complete ? nextIncomplete(current.items, completedIds, index) : index,
        status: complete ? "complete" : "running",
      };
    });
  };

  const revealItem = (item: RecallItem) => {
    if (item.kind === "setting") {
      setInspectorTab("notes");
      return;
    }
    const view: ViewMode = item.kind === "normal" ? "normalled" : "panel";
    const revealId =
      item.kind === "cable" && !cables.some((cable) => cable.id === item.targetId)
        ? (item.revealTargetId ?? item.targetId)
        : item.targetId;
    const control = definition.controls.find((candidate) => candidate.id === revealId);
    const port = definition.ports.find((candidate) => candidate.id === revealId);
    const sectionId = item.sectionId ?? control?.sectionId ?? port?.sectionId;
    if (sectionId) focusSection(sectionId);
    select(revealId);
    setViewMode(view);
  };

  const rerunSession = () => {
    if (session?.mode === "transition") {
      const target = activeDocument.performanceStates.find(
        (state) => state.id === session.targetStateId,
      );
      if (target) {
        setSelectedStateId(target.id);
        const items = createPerformanceTransitionItems(
          definition,
          {
            controlValues,
            cables,
            customSettings: activeDocument.customSettings,
          },
          target,
        );
        setSession({
          items,
          completedIds: [],
          currentIndex: 0,
          status: items.length ? "running" : "complete",
          mode: "transition",
          targetStateId: target.id,
          targetStateName: target.name,
        });
        return;
      }
    }
    startRecall();
  };

  const currentItem = session?.items[session.currentIndex] ?? null;
  const completedCount = session?.completedIds.length ?? 0;
  const progress = session?.items.length ? (completedCount / session.items.length) * 100 : 100;
  const controlColumns = [
    changedControls.filter((_, index) => index % 2 === 0),
    changedControls.filter((_, index) => index % 2 === 1),
  ];
  const sessionKindCount = (kind: RecallItem["kind"]) =>
    session?.items.filter((item) => item.kind === kind).length ?? 0;
  const itemKindLabel = (item: RecallItem) =>
    item.action ? `${item.action} ${item.kind}` : item.kind;
  const transitionCount = (kind: PerformanceTransitionItem["kind"]) =>
    transitionItems.filter((item) => item.kind === kind).length;
  const transitionCountLabel = (kind: PerformanceTransitionItem["kind"]) => {
    const count = transitionCount(kind);
    return `${count} ${kind}${count === 1 ? "" : "s"}`;
  };

  return (
    <section
      className={`recall-dock${recallOpen ? " is-open" : ""}${session ? " has-session" : ""}`}
    >
      <button
        aria-expanded={recallOpen}
        className="recall-titlebar"
        onClick={toggleRecall}
        type="button"
      >
        <span>
          <Icon name="list" size={15} /> Recall sheet
        </span>
        <small>
          {session
            ? `${completedCount} / ${session.items.length} verified`
            : `${changedControls.length} changed controls · ${cables.length} cables · ${activeDocument.performanceStates.length} states`}
        </small>
        <Icon name="chevron" size={15} />
      </button>

      {recallOpen && session ? (
        session.status === "complete" ? (
          <div className="recall-content recall-complete" role="status">
            <div className="recall-complete-mark">
              <Icon name="check" size={24} />
            </div>
            <div>
              <span className="eyebrow">Recall complete</span>
              <h3>
                {session.items.length
                  ? session.mode === "transition"
                    ? `Transition to ${session.targetStateName} checked`
                    : `All ${session.items.length} items verified`
                  : "Nothing needs recalling"}
              </h3>
              <p>
                {session.items.length
                  ? session.mode === "transition"
                    ? "The control, cable, and structured-setting differences in this transition have been checked. Apply the exact saved state if you want the document to match it automatically."
                    : "The documented controls, patch leads, and overridden normals in this snapshot have been checked."
                  : session.mode === "transition"
                    ? "The current patch already matches this saved performance state."
                    : "This patch has no changed controls, patch leads, or broken internal normals."}
              </p>
            </div>
            <div className="recall-complete-actions">
              {session.mode === "transition" && session.targetStateId && (
                <button
                  className="is-primary"
                  onClick={() => applyPerformanceState(session.targetStateId!)}
                  type="button"
                >
                  Apply exact state
                </button>
              )}
              <button onClick={rerunSession} type="button">
                Run again
              </button>
              <button onClick={() => setSession(null)} type="button">
                Back to sheet
              </button>
              <button onClick={toggleRecall} type="button">
                Close
              </button>
            </div>
          </div>
        ) : (
          <div className="recall-content recall-session">
            <section className="recall-progress-panel">
              <span className="eyebrow">Recall progress</span>
              <strong>
                {completedCount}
                <small> / {session.items.length}</small>
              </strong>
              <div
                aria-label="Recall progress"
                aria-valuemax={session.items.length}
                aria-valuemin={0}
                aria-valuenow={completedCount}
                className="recall-progress-bar"
                role="progressbar"
              >
                <i style={{ width: `${progress}%` }} />
              </div>
              <dl>
                <div>
                  <dt>Controls</dt>
                  <dd>{sessionKindCount("control")}</dd>
                </div>
                <div>
                  <dt>Cables</dt>
                  <dd>{sessionKindCount("cable")}</dd>
                </div>
                {session.mode === "transition" ? (
                  <div>
                    <dt>Settings</dt>
                    <dd>{sessionKindCount("setting")}</dd>
                  </div>
                ) : (
                  <div>
                    <dt>Normals</dt>
                    <dd>{sessionKindCount("normal")}</dd>
                  </div>
                )}
              </dl>
              <button className="recall-cancel" onClick={() => setSession(null)} type="button">
                Stop recall
              </button>
            </section>

            {currentItem && (
              <section className="recall-current-item">
                <header>
                  <span>{itemKindLabel(currentItem)}</span>
                  <small>
                    Item {session.currentIndex + 1} of {session.items.length}
                  </small>
                </header>
                <div>
                  {currentItem.colour && <i style={{ background: currentItem.colour }} />}
                  <h3>{currentItem.label}</h3>
                  <p>{currentItem.detail}</p>
                </div>
                <footer>
                  <button
                    onClick={() =>
                      setSession((current) =>
                        current
                          ? { ...current, currentIndex: Math.max(0, current.currentIndex - 1) }
                          : current,
                      )
                    }
                    disabled={session.currentIndex === 0}
                    type="button"
                  >
                    Previous
                  </button>
                  <button onClick={() => revealItem(currentItem)} type="button">
                    {currentItem.kind === "setting" ? "Open settings" : "Show on panel"}
                  </button>
                  <button
                    className="recall-verify"
                    onClick={() => toggleItem(currentItem.id, session.currentIndex, true)}
                    type="button"
                  >
                    <Icon name="check" size={15} /> Verify & next
                  </button>
                </footer>
              </section>
            )}

            <section className="recall-checklist">
              <header>
                <h3>Checklist</h3>
                <small>Snapshot at start</small>
              </header>
              <ol>
                {session.items.map((item, index) => {
                  const complete = session.completedIds.includes(item.id);
                  return (
                    <li
                      className={`${index === session.currentIndex ? "is-current" : ""}${complete ? " is-complete" : ""}`}
                      key={item.id}
                    >
                      <button
                        aria-pressed={complete}
                        onClick={() => toggleItem(item.id, index)}
                        type="button"
                      >
                        <i>{complete && <Icon name="check" size={12} />}</i>
                        <span>{item.label}</span>
                        <small>{itemKindLabel(item)}</small>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </section>
          </div>
        )
      ) : recallOpen ? (
        <div className={`recall-content${selectedState ? " has-state-target" : ""}`}>
          {selectedState ? (
            <section
              aria-labelledby="recall-state-target-heading"
              className="recall-column recall-state-target"
            >
              <h3 id="recall-state-target-heading">Performance transition</h3>
              <label>
                <span>Target state</span>
                <select
                  onChange={(event) => setSelectedStateId(event.target.value)}
                  value={selectedState.id}
                >
                  {activeDocument.performanceStates.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </label>
              <strong>{selectedState.name}</strong>
              <p>{selectedState.notes || "No transition notes documented."}</p>
              <div className="state-transition-summary">
                <span>{transitionCountLabel("control")}</span>
                <span>{transitionCountLabel("cable")}</span>
                <span>{transitionCountLabel("setting")}</span>
              </div>
              <small>
                {transitionItems.length
                  ? `${transitionItems.length} step${transitionItems.length === 1 ? "" : "s"} differ${transitionItems.length === 1 ? "s" : ""} from the current patch`
                  : "Current patch matches this state"}
              </small>
            </section>
          ) : (
            controlColumns.map((controls, columnIndex) => (
              <div className="recall-column" key={columnIndex}>
                <h3>{columnIndex === 0 ? "Changed controls" : "Continued"}</h3>
                {controls.length ? (
                  <dl>
                    {controls.map((control) => (
                      <div key={control.id}>
                        <dt>{control.label}</dt>
                        <dd>{describeControl(control)}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="recall-empty">No further changes</p>
                )}
              </div>
            ))
          )}
          {selectedState && (
            <div className="recall-column">
              <h3>Current changed controls</h3>
              {changedControls.length ? (
                <dl>
                  {changedControls.map((control) => (
                    <div key={control.id}>
                      <dt>{control.label}</dt>
                      <dd>{describeControl(control)}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="recall-empty">All controls at documented defaults</p>
              )}
            </div>
          )}
          <div className="recall-column cable-recall">
            <h3>Patch cables</h3>
            <ol>
              {cables.map((cable) => (
                <li key={cable.id}>
                  <i style={{ background: cable.colour }} /> {describeEndpoint(cable.endpointA)}{" "}
                  <span>→</span> {describeEndpoint(cable.endpointB)}
                  {cable.branches?.map((branch, index) => (
                    <small key={index}>
                      {" "}
                      · branch {index + 2}: {describeEndpoint(branch)}
                    </small>
                  ))}
                </li>
              ))}
            </ol>
            <h3 className="normal-summary-heading">Internal normals</h3>
            <p className={`normal-summary${brokenNormals.length ? " has-breaks" : ""}`}>
              {brokenNormals.length
                ? brokenNormals.map((state) => state.route.label).join(" · ")
                : `All ${normalStates.length} normal routes active`}
            </p>
            {warnings.length > 0 && (
              <p className="routing-warning-summary">
                {warnings.filter((warning) => warning.severity === "warning").length} warnings ·{" "}
                {warnings.filter((warning) => warning.severity === "info").length} notes
              </p>
            )}
          </div>
          <div className="recall-actions">
            {selectedState && (
              <>
                <button onClick={startTransition} type="button">
                  <Icon name="list" size={15} /> Start transition
                </button>
                <button onClick={() => applyPerformanceState(selectedState.id)} type="button">
                  <Icon name="check" size={15} /> Apply state now
                </button>
              </>
            )}
            <button onClick={startRecall} type="button">
              <Icon name="check" size={15} /> Start recall
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
